import { TrendingUp, TrendingDown, X } from "lucide-react";
import clsx from "clsx";
import { formatPrice, formatPercent } from "@/lib/format";
import { livePnl, rrRatio } from "@/lib/stats";
import type { OpenTrade } from "@/store/trading";

interface LivePnLProps {
  open: OpenTrade;
  /** True when SL/TP auto-close is running (replay). False = lines are visual/journal only. */
  autoCloseActive?: boolean;
  currentPrice: number | null;
  onUpdateSL: (price: number | null) => void;
  onUpdateTP: (price: number | null) => void;
  onClose: () => void;
}

export function LivePnL({ open, autoCloseActive, currentPrice, onUpdateSL, onUpdateTP, onClose }: LivePnLProps) {
  const price = currentPrice ?? open.entryPrice;
  const { pnl, pnlPct } = livePnl(open.direction, open.entryPrice, price, open.quantity);
  const isUp = pnl >= 0;
  const rr = rrRatio(open.direction, open.entryPrice, open.slPrice, open.tpPrice);

  return (
    <div
      className={clsx(
        "rounded-lg border bg-bg-elevated p-3 animate-fade-in",
        open.direction === "long" ? "border-bull/40" : "border-bear/40"
      )}
    >
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className={clsx(
              "rounded px-1.5 py-0.5 text-2xs font-bold uppercase tracking-wider",
              open.direction === "long" ? "bg-bull/20 text-bull" : "bg-bear/20 text-bear"
            )}
          >
            {open.direction === "long" ? "LONG" : "SHORT"}
          </span>
          <span className="font-mono text-xs text-text-primary">
            {open.quantity} @ {formatPrice(open.entryPrice)}
          </span>
        </div>
        <button
          onClick={onClose}
          className="rounded p-1 text-text-muted hover:bg-bg-hover hover:text-bear"
          title="Close position at current price"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className={clsx("mb-3 flex items-baseline gap-2", isUp ? "text-bull" : "text-bear")}>
        {isUp ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
        <span className="font-mono text-lg font-bold tabular">
          {isUp ? "+" : ""}{formatPrice(pnl)}
        </span>
        <span className="font-mono text-xs opacity-80">
          ({formatPercent(pnlPct)})
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <PriceLevelInput
          label="Stop loss"
          value={open.slPrice}
          accent="bear"
          onChange={onUpdateSL}
        />
        <PriceLevelInput
          label="Take profit"
          value={open.tpPrice}
          accent="bull"
          onChange={onUpdateTP}
        />
      </div>

      {!autoCloseActive && (open.slPrice !== null || open.tpPrice !== null) && (
        <div className="mt-2 rounded bg-warn/10 px-2 py-1 text-2xs text-warn">
          SL/TP auto-close runs in replay only — exit manually in live mode.
        </div>
      )}

      <div className="mt-2 flex justify-between text-2xs text-text-muted">
        <span>Current: <span className="font-mono text-text-primary">{formatPrice(price)}</span></span>
        {rr > 0 && (
          <span>
            R:R{" "}
            <span className="font-mono font-semibold text-accent">
              1:{rr.toFixed(2)}
            </span>
          </span>
        )}
      </div>
    </div>
  );
}

function PriceLevelInput({
  label,
  value,
  accent,
  onChange,
}: {
  label: string;
  value: number | null;
  accent: "bull" | "bear";
  onChange: (price: number | null) => void;
}) {
  const accentClass = accent === "bull"
    ? "border-bull/40 focus:border-bull focus:ring-bull"
    : "border-bear/40 focus:border-bear focus:ring-bear";

  return (
    <label className="flex flex-col gap-1">
      <span className="text-2xs uppercase tracking-wider text-text-muted">{label}</span>
      <input
        type="number"
        step="0.01"
        value={value ?? ""}
        placeholder="—"
        onChange={(e) => {
          const v = e.target.value;
          onChange(v === "" ? null : Number(v));
        }}
        className={clsx(
          "rounded-md border bg-bg-deep px-2 py-1 text-xs font-mono tabular text-text-primary focus:outline-none focus:ring-1",
          accentClass
        )}
      />
    </label>
  );
}
